import type { MyTxtPermission, MyTxtResource } from './permissions.types';

export type MothershipAppInfo = {
  slug: string;
  displayName: string;
  resource: MyTxtResource;
};

/**
 * Mirrors txt-core-backend's src/config/mothership-apps.registry.ts (slug +
 * display name only — the frontend doesn't need the env var names). Slugs
 * are the app's product name, not the student's name.
 *
 * Student portal: `/scla/mobile-applications` locks to the single app the
 * user can read (team-derived `*-app-admin` grant). There is no app switcher.
 * Team-derived `*-app-admin:write` implies read.
 */
export const MOTHERSHIP_APPS: MothershipAppInfo[] = [
  {
    slug: 'airaware',
    displayName: 'AirAware',
    resource: 'airaware-app-admin',
  },
  {
    slug: 'scrollblock',
    displayName: 'ScrollBlock',
    resource: 'scrollblock-app-admin',
  },
  {
    slug: 'skillsprout',
    displayName: 'SkillSprout',
    resource: 'skillsprout-app-admin',
  },
  {
    slug: 'indie-cardz',
    displayName: 'IndieCardz',
    resource: 'indie-cardz-app-admin',
  },
  {
    slug: 'park-rally',
    displayName: 'ParkRally',
    resource: 'park-rally-app-admin',
  },
];

export const MOTHERSHIP_APP_READ_PERMISSIONS: MyTxtPermission[] =
  MOTHERSHIP_APPS.map(app => `${app.resource}:read` as MyTxtPermission);

const APP_ADMIN_PERM_RE = /^(.+)-app-admin:(?:read|write)$/;

/**
 * Apps the student was actually granted via team-derived `*-app-admin:*`
 * permissions on the session. Does **not** expand `*` or use `can()` —
 * those both treat every registry app as allowed and wrongly lock the
 * student portal to AirAware (first in `MOTHERSHIP_APPS`).
 */
export function resolveTeamGrantedMothershipApps(
  permissions: readonly string[] | null | undefined,
): MothershipAppInfo[] {
  const held = permissions ?? [];
  const grantedSlugs = new Set<string>();

  held.forEach(permission => {
    const match = APP_ADMIN_PERM_RE.exec(permission);
    if (!match) return;
    const slug = match[1];
    if (MOTHERSHIP_APPS.some(app => app.slug === slug)) {
      grantedSlugs.add(slug);
    }
  });

  return MOTHERSHIP_APPS.filter(app => grantedSlugs.has(app.slug));
}

/**
 * The fields each app actually asks for at signup/first-login (verified
 * against each app's own signup path in scla-2026-mothership, not the full
 * user schema — most fields on each model are filled in later via separate
 * profile flows, not at signup):
 * - airaware/indie-cardz: Firebase-token-driven first-login upsert
 *   (email, displayName, isActive) — no dedicated signup body.
 * - scrollblock: schema field is `displayName` (aldo user.model) — product
 *   treats it as the user's username (unique via displayNameLower). Table
 *   labels this column "Username".
 * - skillsprout: signup itself only writes email (+firebaseUid) —
 *   everything else (username, resume, jobs, id verification) is added
 *   afterward. `role` (student/employer/parent) is set moments later via
 *   a post-signup onboarding step (profile.controller.ts), not the raw
 *   signup write, but included here since it's the key parent-vs-student
 *   distinction for this app.
 * - park-rally: dedicated syncUser sets email, name, isActive on insert.
 * Drives which columns the admin table shows per app — not a generic
 * "whatever keys happen to be on the doc" guess.
 */
export const MOTHERSHIP_SIGNUP_FIELDS: Record<string, string[]> = {
  airaware: ['email', 'displayName', 'isActive'],
  scrollblock: ['email', 'displayName', 'isActive'],
  skillsprout: ['email', 'role', 'isActive'],
  'indie-cardz': ['email', 'displayName', 'isActive'],
  'park-rally': ['email', 'name', 'isActive'],
};

/** Column header overrides when the Mongo key differs from the product term. */
export const MOTHERSHIP_FIELD_LABELS: Record<string, Record<string, string>> = {
  scrollblock: { displayName: 'Username' },
};
